import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCreateApiKey } from '@/modules/api-keys/hooks';
import { Form, FormField, TextInput } from '@/modules/forms';
import { Button, ButtonType, Heading, Text } from '@/modules/ui';

import { apiKeyFormSchema, type ApiKeyFormSchema, type ApiKeyFormValues } from '../../../schemas';
import * as sharedRules from '../CreateApiKeyModal.rules';
import * as felaRules from './ApiKeyNameStep.rules';

export interface ApiKeyNameStepProps {
    onCreated: (key: string) => void;
}

export const ApiKeyNameStep = ({ onCreated }: ApiKeyNameStepProps) => {
    const { css } = useFela();
    const { formatMessage } = useIntl();

    const createMutation = useCreateApiKey();

    // `mutate`, not `mutateAsync`: react-hook-form rethrows out of its submit handler, so a
    // rejecting `mutateAsync` would surface as an unhandled rejection unless caught here.
    const handleCreate = ({ name }: ApiKeyFormValues) => {
        createMutation.mutate({ data: { name } }, { onSuccess: result => onCreated(result.data.key) });
    };

    return (
        <Form<ApiKeyFormSchema, ApiKeyFormValues>
            schema={apiKeyFormSchema}
            defaultValues={{ name: '' }}
            onSubmit={handleCreate}
            extend={{ form: sharedRules.content }}
        >
            <div className={css(sharedRules.body)}>
                <Heading elementType='h1' variant='h2' color='black'>
                    <FormattedMessage id='api-keys.create.name.title' />
                </Heading>
                <div className={css(sharedRules.group)}>
                    <Text color='foregroundsTertiary' customStyle={sharedRules.paragraph}>
                        <FormattedMessage id='api-keys.create.name.description' />
                    </Text>
                    <FormField<ApiKeyFormValues> name='name' customStyle={felaRules.field}>
                        <TextInput
                            name='name'
                            placeholder={formatMessage({ id: 'api-keys.create.name.placeholder' })}
                            aria-label={formatMessage({ id: 'api-keys.create.name.label' })}
                            customStyle={felaRules.nameInput}
                        />
                    </FormField>
                </div>
            </div>
            <Button type={ButtonType.PRIMARY} htmlType='submit' loading={createMutation.isPending} customStyle={sharedRules.submitButton}>
                <FormattedMessage id='api-keys.create.submit' />
            </Button>
        </Form>
    );
};
